//mushroom-strains-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  useGridApiRef,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingBackdrop from "../components/loading-backdrop";
import Page from "../components/page";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { useHistory } from "react-router-dom";

interface MushroomStrain {
  id: number;
  name: string;
  species: string | null;
  notes: string | null;
  archivedAt: string | null;

  daysAgarPlate: number | null;
  daysLiquidCulture: number | null;
  daysGrainSpawn: number | null;
  daysBulkSpawn: number | null;
  daysFruitingBlockColonization: number | null;
  daysCasing: number | null;
  daysFruitingToFirstHarvest: number | null;
  harvestWindowDays: number | null;
  daysFirstToSecondFlush: number | null;
  daysSecondToThirdFlush: number | null;
}

const strainsGridStateStorageKey = "mushroomStrainsGridState";
const strainsGridDensityStorageKey = "mushroomStrainsGridDensity";
const mushroomStrainsStorageKey = "mushroomStrains";

type MushroomWorkflowSettings = {
  useAgar?: boolean;
  useLiquidCulture?: boolean;
  useGrainSpawn?: boolean;
  useBulkSpawn?: boolean;
  useFruitingBlock?: boolean;
  useCasing?: boolean;
  useFruiting?: boolean;
};

const mushroomWorkflowSettingsStorageKey = "mushroomWorkflowDefaults";

function loadMushroomWorkflowSettings(): MushroomWorkflowSettings {
  try {
    const raw = window.localStorage.getItem(mushroomWorkflowSettingsStorageKey);
    if (!raw) {
      return {
        useAgar: false,
        useLiquidCulture: false,
        useGrainSpawn: false,
        useBulkSpawn: false,
        useFruitingBlock: false,
        useCasing: false,
        useFruiting: true, // ✅ only this on
      };
    }

    const parsed = JSON.parse(raw) as MushroomWorkflowSettings;

    return {
      useAgar: parsed.useAgar ?? false,
      useLiquidCulture: parsed.useLiquidCulture ?? false,
      useGrainSpawn: parsed.useGrainSpawn ?? false,
      useBulkSpawn: parsed.useBulkSpawn ?? false,
      useFruitingBlock: parsed.useFruitingBlock ?? false,
      useCasing: parsed.useCasing ?? false,
      useFruiting: parsed.useFruiting ?? true,
    };
  } catch {
    return {
      useAgar: false,
      useLiquidCulture: false,
      useGrainSpawn: false,
      useBulkSpawn: false,
      useFruitingBlock: false,
      useCasing: false,
      useFruiting: true,
    };
  }
}

function getActiveStrainFieldsFromWorkflow(w: MushroomWorkflowSettings) {
  return {
    species: false,
    notes: false,

    daysAgarPlate: false,
    daysLiquidCulture: false,

    daysGrainSpawn: true,
    daysBulkSpawn: false,

    daysFruitingBlockColonization: true,
    daysCasing: false,

    daysFruitingToFirstHarvest: true,
    harvestWindowDays: true,
  };
}

const StrainsDataGrid = React.memo(
  (props: {
    strains: MushroomStrain[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const csvFileName = React.useMemo(() => {
      const now = DateTime.local();

      return `SeedLeaf Mushroom Strains - ${now.toFormat("MMM dd, yyyy")}`;
    }, []);

    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(strainsGridStateStorageKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;
            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }
        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 25 },
          };
        }

        localStorage.setItem(
          strainsGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(strainsGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(strainsGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,

            daysAgarPlate: false,
            daysLiquidCulture: false,
            daysGrainSpawn: false,
            daysBulkSpawn: false,
            daysFruitingBlockColonization: false,
            daysCasing: false,
            daysFruitingToFirstHarvest: false,
            harvestWindowDays: false,
            daysFirstToSecondFlush: false,
            daysSecondToThirdFlush: false,

            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },

          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "name", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        strainsGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "name",
        headerName: "Name",
        width: 260,
      },
      {
        field: "species",
        headerName: "Species",
        width: 260,
      },
      { field: "daysAgarPlate", headerName: "Agar / Plate (days)", width: 170 },
      {
        field: "daysLiquidCulture",
        headerName: "Liquid Culture (days)",
        width: 190,
      },
      { field: "daysGrainSpawn", headerName: "Grain Spawn (days)", width: 180 },
      { field: "daysBulkSpawn", headerName: "Bulk Spawn (days)", width: 170 },
      {
        field: "daysFruitingBlockColonization",
        headerName: "Fruiting Block (Colonization) (days)",
        width: 280,
      },
      { field: "daysCasing", headerName: "Casing (days)", width: 150 },
      {
        field: "daysFruitingToFirstHarvest",
        headerName: "Fruiting → First Harvest (days)",
        width: 230,
      },
      {
        field: "daysFirstToSecondFlush",
        headerName: "First → Second Flush (days)",
        width: 230,
      },
      {
        field: "daysSecondToThirdFlush",
        headerName: "Second → Third Flush (days)",
        width: 240,
      },

      {
        field: "harvestWindowDays",
        headerName: "Harvest Window (days)",
        width: 200,
      },

      {
        field: "notes",
        headerName: "Notes",
        width: 320,
      },
    ];

    if (!props.strains) return null;

    if (!initialState) {
      return <CircularProgress />;
    }

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.strains.map((s) => ({
          id: s.id,
          name: s.name,
          species: s.species,

          daysAgarPlate: s.daysAgarPlate,
          daysLiquidCulture: s.daysLiquidCulture,
          daysGrainSpawn: s.daysGrainSpawn,
          daysBulkSpawn: s.daysBulkSpawn,
          daysFruitingBlockColonization: s.daysFruitingBlockColonization,
          daysCasing: s.daysCasing,
          daysFruitingToFirstHarvest: s.daysFruitingToFirstHarvest,
          harvestWindowDays: s.harvestWindowDays,
          daysFirstToSecondFlush: s.daysFirstToSecondFlush,
          daysSecondToThirdFlush: s.daysSecondToThirdFlush,

          notes: s.notes,
          archivedAt: s.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(strainsGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

const FieldRow = React.memo(
  (props: { active?: boolean; children: React.ReactNode }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ flex: "0 0 70%" }}>{props.children}</Box>
      <Box sx={{ flex: "1 1 auto" }}>
        {props.active ? (
          <Chip size="small" label="Active" color="success" variant="filled" />
        ) : null}
      </Box>
    </Box>
  ),
);

export default function MushroomStrainsPage() {
  const [strains, setStrains] = useState<MushroomStrain[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const showArchivedCheckedStorageKey = "mushroomStrainsShowArchivedChecked";
  const showActiveCheckedStorageKey = "mushroomStrainsShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const lastFocusedInputRef = React.useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addSpecies, setAddSpecies] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [addDaysAgarPlate, setAddDaysAgarPlate] = useState("14");
  const [addDaysLiquidCulture, setAddDaysLiquidCulture] = useState("10");
  const [addDaysGrainSpawn, setAddDaysGrainSpawn] = useState("14");
  const [addDaysBulkSpawn, setAddDaysBulkSpawn] = useState("0");
  const [
    addDaysFruitingBlockColonization,
    setAddDaysFruitingBlockColonization,
  ] = useState("14");
  const [addDaysCasing, setAddDaysCasing] = useState("0");
  const [addDaysFruitingToFirstHarvest, setAddDaysFruitingToFirstHarvest] =
    useState("7");
  const [addDaysFirstToSecondFlush, setAddDaysFirstToSecondFlush] =
    useState("7");
  const [addDaysSecondToThirdFlush, setAddDaysSecondToThirdFlush] =
    useState("7");

  const [addHarvestWindowDays, setAddHarvestWindowDays] = useState("5");
  const [activeFields, setActiveFields] = useState(() =>
    getActiveStrainFieldsFromWorkflow(loadMushroomWorkflowSettings()),
  );

  const history = useHistory();
  let initialPage = getSearchParamNumber(history, "page", 0);

  useEffect(() => {
    const raw = window.localStorage.getItem(mushroomStrainsStorageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as MushroomStrain[];
        setStrains(parsed);
        return;
      } catch {
        window.localStorage.removeItem(mushroomStrainsStorageKey);
      }
    }

    const defaults: MushroomStrain[] = [
      {
        id: 1,
        name: "Blue Oyster",
        species: "Pleurotus ostreatus",
        notes: null,
        archivedAt: null,

        daysAgarPlate: 14,
        daysLiquidCulture: 10,
        daysGrainSpawn: 14,
        daysBulkSpawn: 0,
        daysFruitingBlockColonization: 14,
        daysCasing: 0,
        daysFruitingToFirstHarvest: 7,
        harvestWindowDays: 5,
        daysFirstToSecondFlush: 7,
        daysSecondToThirdFlush: 7,
      },
      {
        id: 2,
        name: "Lion’s Mane",
        species: "Hericium erinaceus",
        notes: null,
        archivedAt: null,

        daysAgarPlate: 21,
        daysLiquidCulture: 14,
        daysGrainSpawn: 21,
        daysBulkSpawn: 0,
        daysFruitingBlockColonization: 21,
        daysCasing: 0,
        daysFruitingToFirstHarvest: 10,
        harvestWindowDays: 7,
        daysFirstToSecondFlush: 7,
        daysSecondToThirdFlush: 7,
      },
    ];

    setStrains(defaults);
    window.localStorage.setItem(
      mushroomStrainsStorageKey,
      JSON.stringify(defaults),
    );
  }, []);

  useEffect(() => {
    if (!addOpen) return;

    const onFocusIn = (e: any) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;

      // eslint-disable-next-line no-console
      console.log(
        "[Strains Add focusin]",
        el.tagName,
        (el as any).type || "",
        el.id || "",
        el.getAttribute?.("aria-label") || "",
        el.className || "",
      );
    };

    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [addOpen]);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    history.push(`/mushroom/home/strains/${id}`);
  };

  const handleClickAdd = () => {
    setAddName("");
    setAddSpecies("");
    setAddNotes("");

    setAddDaysAgarPlate("14");
    setAddDaysLiquidCulture("10");
    setAddDaysGrainSpawn("14");
    setAddDaysBulkSpawn("0");
    setAddDaysFruitingBlockColonization("14");
    setAddDaysCasing("0");
    setAddDaysFruitingToFirstHarvest("7");
    setAddHarvestWindowDays("5");
    setAddDaysFirstToSecondFlush("7");
    setAddDaysSecondToThirdFlush("7");

    lastFocusedInputRef.current = null;

    setAddOpen(true);

    setActiveFields(
      getActiveStrainFieldsFromWorkflow(loadMushroomWorkflowSettings()),
    );

    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!strains) return;

    if (!addName.trim()) {
      return;
    }

    setBusy(true);

    try {
      const maxId = strains.reduce((m, s) => Math.max(m, s.id), 0);

      const next: MushroomStrain = {
        id: maxId + 1,
        name: addName.trim(),
        species: addSpecies.trim() ? addSpecies.trim() : null,
        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,

        daysAgarPlate: Number(addDaysAgarPlate) || 0,
        daysLiquidCulture: Number(addDaysLiquidCulture) || 0,
        daysGrainSpawn: Number(addDaysGrainSpawn) || 0,
        daysBulkSpawn: Number(addDaysBulkSpawn) || 0,
        daysFruitingBlockColonization:
          Number(addDaysFruitingBlockColonization) || 0,
        daysCasing: Number(addDaysCasing) || 0,
        daysFruitingToFirstHarvest: Number(addDaysFruitingToFirstHarvest) || 0,
        harvestWindowDays: Number(addHarvestWindowDays) || 0,
        daysFirstToSecondFlush: Number(addDaysFirstToSecondFlush) || 0,
        daysSecondToThirdFlush: Number(addDaysSecondToThirdFlush) || 0,
      };

      const nextStrains = [next, ...strains];
      setStrains(nextStrains);
      window.localStorage.setItem(
        mushroomStrainsStorageKey,
        JSON.stringify(nextStrains),
      );
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="Strains">
      {strains && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push("/mushroom/home/strains/new")}
              disabled={busy}
            >
              Add
            </Button>

            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={() => {
                return;
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          {!addOpen && (
            <Box sx={{ height: 750, width: "100%", pt: 2 }}>
              <StrainsDataGrid
                strains={strains.filter(
                  (strain) =>
                    (showActiveChecked && strain.archivedAt === null) ||
                    (showArchivedChecked && strain.archivedAt !== null),
                )}
                onSelectionChange={handleSelectionChange}
                initialPage={initialPage}
                onPaginationModelChange={(m) => {
                  const params = new URLSearchParams(`page=${m.page}`);
                  history.replace({
                    search: params.toString(),
                  });
                }}
                onClickRow={onClickRow}
              />
            </Box>
          )}

          <Dialog
            open={addOpen}
            onClose={handleCloseAdd}
            fullWidth
            maxWidth="sm"
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogTitle>Add Strain</DialogTitle>
            <DialogContent>
              <Box
                sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
                onFocusCapture={(e) => {
                  const t = e.target as any;

                  if (
                    t &&
                    (t.tagName === "INPUT" || t.tagName === "TEXTAREA")
                  ) {
                    lastFocusedInputRef.current = t;
                  }
                }}
              >
                <TextField
                  label="Name"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Species"
                  value={addSpecies}
                  onChange={(e) => setAddSpecies(e.target.value)}
                  fullWidth
                />

                <FieldRow active={activeFields.daysAgarPlate}>
                  <TextField
                    label="Agar / Plate (days)"
                    value={addDaysAgarPlate}
                    onChange={(e) => setAddDaysAgarPlate(e.target.value)}
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.daysLiquidCulture}>
                  <TextField
                    label="Liquid Culture (days)"
                    value={addDaysLiquidCulture}
                    onChange={(e) => setAddDaysLiquidCulture(e.target.value)}
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.daysGrainSpawn}>
                  <TextField
                    label="Grain Spawn (days)"
                    value={addDaysGrainSpawn}
                    onChange={(e) => setAddDaysGrainSpawn(e.target.value)}
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.daysBulkSpawn}>
                  <TextField
                    label="Bulk Spawn (days)"
                    value={addDaysBulkSpawn}
                    onChange={(e) => setAddDaysBulkSpawn(e.target.value)}
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.daysFruitingBlockColonization}>
                  <TextField
                    label="Fruiting Block (Colonization) (days)"
                    value={addDaysFruitingBlockColonization}
                    onChange={(e) =>
                      setAddDaysFruitingBlockColonization(e.target.value)
                    }
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.daysCasing}>
                  <TextField
                    label="Casing (days)"
                    value={addDaysCasing}
                    onChange={(e) => setAddDaysCasing(e.target.value)}
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.daysFruitingToFirstHarvest}>
                  <TextField
                    label="Fruiting (to first harvest) (days)"
                    value={addDaysFruitingToFirstHarvest}
                    onChange={(e) =>
                      setAddDaysFruitingToFirstHarvest(e.target.value)
                    }
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={true}>
                  <TextField
                    label="First → Second Flush (days)"
                    value={addDaysFirstToSecondFlush}
                    onChange={(e) =>
                      setAddDaysFirstToSecondFlush(e.target.value)
                    }
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={true}>
                  <TextField
                    label="Second → Third Flush (days)"
                    value={addDaysSecondToThirdFlush}
                    onChange={(e) =>
                      setAddDaysSecondToThirdFlush(e.target.value)
                    }
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.harvestWindowDays}>
                  <TextField
                    label="Harvest Window (days)"
                    value={addHarvestWindowDays}
                    onChange={(e) => setAddHarvestWindowDays(e.target.value)}
                    fullWidth
                  />
                </FieldRow>

                <FieldRow active={activeFields.notes}>
                  <TextField
                    label="Notes"
                    value={addNotes}
                    onChange={(e) => setAddNotes(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </FieldRow>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdd} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAdd}
                color="primary"
                variant="contained"
                disabled={busy || !addName.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
